import { Case } from '@debbie/common/dist/cases/index.js'
import {
  Customer,
  EditNewCustomer,
} from '@debbie/common/dist/customers/index.js'
import {
  CaseVoucher,
  enteredAmount,
} from '@debbie/common/dist/economy/case-vouchers/index.js'
import {
  VoucherWithChildren,
  getVoucherLabel,
  groupVouchersByParent,
  isPayment,
} from '@debbie/common/dist/economy/helpers/vouchers.js'
import { VoucherTypeIds } from '@debbie/common/dist/economy/vouchers/index.js'
import { Entity, formatNumber } from '@debbie/common/dist/index.js'
import { SearchItem } from '@debbie/common/dist/search/index.js'
import { useMutation } from '@debbie/common/dist/util/hooks.js'
import * as casesAPI from '@debbie/cortex/dist/api/cases.js'
import * as customersAPI from '@debbie/cortex/dist/api/customers.js'
import { useCaseVouchersByCase } from '@debbie/cortex/dist/case-vouchers/index.js'
import { c, h } from '@debbie/cortex/dist/index.js'
import { RootState } from '@debbie/cortex/dist/reducer.js'
import CheckboxSimple from '@debbie/iris/dist/components/CheckboxSimple.js'
import CreateCustomer from '@debbie/iris/dist/components/Customers/CreateCustomer.js'
import { X } from '@debbie/iris/dist/components/Icons/index.js'
import {
  Button,
  CasePreview,
  Modal,
  Search,
} from '@debbie/iris/dist/components/index.js'
import PillSelector from '@debbie/iris/dist/components/PillSelector.js'
import { errorNotification } from '@debbie/iris/dist/utils/errors.js'
import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { styled } from 'styled-components'

type Props = {
  case: Case
  onClose: () => void
  reloadCase: (caseId: string, ensureLoaded?: boolean) => Promise<void>
}

type Mode = 'join' | 'create'

const AssociatedCaseModal = (props: Props) => {
  const { case: singleCase, onClose, reloadCase } = props

  const lang = useSelector((s: RootState) => s.lang)

  const [mode, setMode] = useState<Mode>('join')

  // Join existing case state
  const [toCase, setToCase] = useState<SearchItem | null>(null)

  // Create new associated case state
  const [newCustomerData, setNewCustomerData] =
    useState<EditNewCustomer | null>(null)
  const [existingCustomer, setExistingCustomer] = useState<Customer | null>(
    null,
  )
  const [selectedVoucherIds, setSelectedVoucherIds] = useState<Set<string>>(
    new Set(),
  )
  const [vouchersInitialized, setVouchersInitialized] = useState(false)

  // Fetch source case vouchers for the create mode
  const caseVouchersLoader = useCaseVouchersByCase(singleCase.caseId)
  const allVouchers = h.hasData(caseVouchersLoader)
    ? caseVouchersLoader.data
    : []

  const cloneableVouchers = useMemo(
    () =>
      allVouchers.filter(
        (v) =>
          !isPayment(v) &&
          v.voucherTypeId !== VoucherTypeIds.CREDITNOTE &&
          v.voucherTypeId !== VoucherTypeIds.JOINTDEPOSIT &&
          v.deletedAt === null,
      ),
    [allVouchers],
  )
  const groupedVouchers = useMemo(
    () => groupVouchersByParent(cloneableVouchers),
    [cloneableVouchers],
  )

  useEffect(() => {
    if (!vouchersInitialized && cloneableVouchers.length > 0) {
      setSelectedVoucherIds(
        new Set(cloneableVouchers.map((v) => v.caseVoucherId)),
      )
      setVouchersInitialized(true)
    }
  }, [vouchersInitialized, cloneableVouchers])

  const toggleParentVoucher = (parent: VoucherWithChildren<CaseVoucher>) => {
    setSelectedVoucherIds((prev) => {
      const next = new Set(prev)
      if (next.has(parent.caseVoucherId)) {
        next.delete(parent.caseVoucherId)
        for (const child of parent.children) {
          next.delete(child.caseVoucherId)
        }
      } else {
        next.add(parent.caseVoucherId)
        for (const child of parent.children) {
          next.add(child.caseVoucherId)
        }
      }
      return next
    })
  }

  const toggleChildVoucher = (childId: string) => {
    setSelectedVoucherIds((prev) => {
      const next = new Set(prev)
      if (next.has(childId)) {
        next.delete(childId)
      } else {
        next.add(childId)
      }
      return next
    })
  }

  // Join existing case mutation
  const associateMutation = useMutation(
    async (withCaseId: string) => {
      const withCase = await casesAPI.getById(withCaseId)

      const associationId =
        singleCase.associationId ||
        withCase.associationId ||
        crypto.randomUUID()

      if (singleCase.associationId !== associationId) {
        await casesAPI.patch(singleCase.caseId, { associationId })
        await reloadCase(singleCase.caseId, true)
      }
      if (withCase.associationId !== associationId) {
        await casesAPI.patch(withCase.caseId, { associationId })
        await reloadCase(withCase.caseId, false)
      }
    },
    {
      onSuccess: () => {
        onClose()
      },
      onError: (err) => {
        errorNotification(lang, err, 'any', lang.errorEntities.associateCase)
      },
    },
  )

  // Create new associated case mutation
  const createMutation = useMutation(
    async () => {
      let customerId: string

      if (existingCustomer !== null) {
        customerId = existingCustomer.customerId
      } else if (newCustomerData !== null) {
        const result = await customersAPI.create(singleCase.creditorId, {
          referenceId: newCustomerData.referenceId,
          isCompany: newCustomerData.isCompany,
          users: newCustomerData.users,
        })
        customerId = result.customerId
      } else {
        throw new Error('No customer selected')
      }

      const result = await casesAPI.cloneAssociated(singleCase.caseId, {
        customerId,
        cloneVoucherIds: Array.from(selectedVoucherIds),
      })

      await reloadCase(singleCase.caseId, true)

      c.redirect.redirect(`/cases/${result.caseSeqId}`)
    },
    {
      onSuccess: () => {
        onClose()
      },
      onError: (err) => {
        errorNotification(lang, err, 'create', lang.errorEntities.case)
      },
    },
  )

  const hasCustomer = existingCustomer !== null || newCustomerData !== null
  const canCreate = hasCustomer && !createMutation.isLoading

  return (
    <Modal
      header={lang.associateCases.associateCase}
      onToggle={onClose}
      maxWidth="600px"
    >
      <PillSelector<Mode>
        value={mode}
        onChange={setMode}
        options={[
          {
            key: 'join',
            value: 'join',
            label: lang.associateCases.joinExistingCase,
          },
          {
            key: 'create',
            value: 'create',
            label: lang.associateCases.createNewAssociatedCase,
          },
        ]}
      />

      <Content>
        {mode === 'join' && (
          <>
            <p>{lang.associateCases.associateWithCase}</p>
            {toCase ? (
              <SelectedCase>
                <CasePreview caseId={toCase.id}></CasePreview>
                <CloseIcon
                  onClick={() => {
                    setToCase(null)
                  }}
                />
              </SelectedCase>
            ) : (
              <Search
                placeholder={lang.navigation.search}
                removeWrapper
                entity={Entity.Case}
                creditorId={singleCase.creditorId}
                onSelect={setToCase}
              />
            )}
            <Button
              disabled={!(toCase && toCase.id)}
              block
              loading={associateMutation.isLoading}
              onClick={() => {
                if (toCase?.id) {
                  associateMutation.mutate(toCase.id)
                }
              }}
            >
              {lang.inputs.save}
            </Button>
          </>
        )}

        {mode === 'create' && (
          <>
            <Section>
              <SectionHeader>
                {lang.associateCases.createNewAssociatedCase}
              </SectionHeader>

              <CreateCustomer
                creditorId={singleCase.creditorId}
                onChange={setNewCustomerData}
                onChangeExistingCustomer={setExistingCustomer}
              />
            </Section>

            <Section>
              <SectionHeader>
                {lang.associateCases.selectVouchers}
              </SectionHeader>
              <Description>
                {lang.associateCases.selectVouchersDescription}
              </Description>

              {groupedVouchers.length === 0 ? (
                <EmptyState>
                  {lang.associateCases.noVouchersAvailable}
                </EmptyState>
              ) : (
                <VoucherList>
                  {groupedVouchers.map((voucher) => {
                    const isSelected = selectedVoucherIds.has(
                      voucher.caseVoucherId,
                    )
                    return (
                      <React.Fragment key={voucher.caseVoucherId}>
                        <VoucherItem
                          onClick={() => toggleParentVoucher(voucher)}
                          $selected={isSelected}
                        >
                          <VoucherCheckbox>
                            <CheckboxSimple
                              checked={isSelected}
                              onClick={(e) => e.stopPropagation()}
                              onChange={() => toggleParentVoucher(voucher)}
                            />
                          </VoucherCheckbox>
                          <VoucherInfoDisplay voucher={voucher} />
                        </VoucherItem>
                        {voucher.children.map((child) => {
                          const isChildSelected = selectedVoucherIds.has(
                            child.caseVoucherId,
                          )
                          return (
                            <VoucherItem
                              key={child.caseVoucherId}
                              onClick={
                                isSelected
                                  ? () =>
                                      toggleChildVoucher(child.caseVoucherId)
                                  : undefined
                              }
                              $selected={isChildSelected}
                              $disabled={!isSelected}
                            >
                              <ChildIndent />
                              <VoucherCheckbox>
                                <CheckboxSimple
                                  checked={isChildSelected}
                                  disabled={!isSelected}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={() =>
                                    toggleChildVoucher(child.caseVoucherId)
                                  }
                                />
                              </VoucherCheckbox>
                              <VoucherInfoDisplay voucher={child} />
                            </VoucherItem>
                          )
                        })}
                      </React.Fragment>
                    )
                  })}
                </VoucherList>
              )}
            </Section>

            <Button
              disabled={!canCreate}
              block
              loading={createMutation.isLoading}
              onClick={() => {
                createMutation.mutate()
              }}
            >
              {lang.associateCases.createAssociatedCaseButton}
            </Button>
          </>
        )}
      </Content>
    </Modal>
  )
}

export default AssociatedCaseModal

const VoucherInfoDisplay = (props: { voucher: CaseVoucher }) => {
  const lang = useSelector((s: RootState) => s.lang)
  return (
    <VoucherInfo>
      <VoucherLabel>
        {getVoucherLabel(props.voucher, lang)}
        {props.voucher.referenceId ? ` (${props.voucher.referenceId})` : ''}
      </VoucherLabel>
      <VoucherAmount>
        {/* The clone takes what was entered; its case writes off its own. */}
        {formatNumber(Math.abs(enteredAmount(props.voucher)))}{' '}
        {props.voucher.currency}
      </VoucherAmount>
    </VoucherInfo>
  )
}

const Content = styled.div`
  margin-top: 1em;
`

const Section = styled.div`
  margin-bottom: 1.5em;
`

const SectionHeader = styled.h4`
  margin: 0 0 0.5em 0;
  font-size: 0.9em;
  font-weight: 600;
`

const SelectedCase = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1em;
  margin-bottom: 1em;
  background: #ddd;
  border-radius: 3px;
`

const VoucherList = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
`

const VoucherItem = styled.div<{ $selected: boolean; $disabled?: boolean }>`
  display: flex;
  align-items: center;
  padding: 0.6em 0.8em;
  cursor: ${(props) => (props.$disabled ? 'default' : 'pointer')};
  background: ${(props) =>
    props.$disabled ? '#f5f5f5' : props.$selected ? '#f0f7ff' : '#fff'};
  opacity: ${(props) => (props.$disabled ? 0.5 : 1)};
  border-bottom: 1px solid #e0e0e0;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${(props) =>
      props.$disabled ? '#f5f5f5' : props.$selected ? '#e5f0ff' : '#f9f9f9'};
  }
`

const ChildIndent = styled.div`
  width: 1.2em;
  flex-shrink: 0;
`

const VoucherCheckbox = styled.div`
  margin-right: 0.6em;
  display: flex;
  align-items: center;
`

const VoucherInfo = styled.div`
  display: flex;
  justify-content: space-between;
  flex: 1;
`

const VoucherLabel = styled.span`
  font-size: 0.85em;
`

const VoucherAmount = styled.span`
  font-size: 0.85em;
  font-weight: 500;
`

const CloseIcon = styled(X)`
  cursor: pointer;
`

const Description = styled.p`
  font-size: 0.85em;
  color: #666;
`

const EmptyState = styled.div`
  padding: 1em;
  text-align: center;
  color: #999;
  font-size: 0.85em;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
`
